import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { UnifiedSlot } from '../InstructorDashboard';
import SlotActionBar from './SlotActionBar';

interface DashboardDayViewProps {
  slots: UnifiedSlot[];
  date: string;
  onDateChange: (date: string) => void;
  onAttendance: (batchIds: number[], templateSlotId?: number) => void;
  onLessonPlan: (batchId: number, lessonPlanId: number, topicId?: number) => void;
  onViewTemplate: (templateId: number) => void;
  onConfirm?: (slot: UnifiedSlot) => void;
  onUnconfirm?: (slot: UnifiedSlot) => void;
}

const PIXELS_PER_30MIN = 40;
const GAP_INDICATOR_HEIGHT = 28;
const BAND_PADDING_MIN = 30; // 30 minutes padding around each slot group

interface TimeBand {
  startMin: number;
  endMin: number;
  yOffset: number; // pixel offset in compact layout
  heightPx: number; // pixel height of this band
}

function parseLocalDate(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function timeToMinutes(time: string): number {
  if (!time) return 0;
  const parts = time.split(':');
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
}

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatTimeDisplay(time: string): string {
  if (!time) return '-';
  const parts = time.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
}

function addDays(dateStr: string, days: number): string {
  const d = parseLocalDate(dateStr);
  d.setDate(d.getDate() + days);
  return toDateKey(d);
}

function formatGapDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}min gap`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `${h}h gap`;
  return `${h}h ${m}min gap`;
}

const STATUS_COLORS: Record<string, { bg: string; border: string }> = {
  planned: { bg: '#f5f5f5', border: '#bdbdbd' },
  'pre-scheduled': { bg: '#fff9c4', border: '#fbc02d' },
  scheduled: { bg: '#e3f2fd', border: '#1976d2' },
  conducted: { bg: '#e8f5e9', border: '#388e3c' },
  cancelled: { bg: '#ffebee', border: '#d32f2f' },
};

const DashboardDayView: React.FC<DashboardDayViewProps> = ({
  slots,
  date,
  onDateChange,
  onAttendance,
  onLessonPlan,
  onViewTemplate,
  onConfirm,
  onUnconfirm,
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [compactMode, setCompactMode] = useState(false);

  // Compute full time axis (used in normal mode)
  const { dayStartMinutes, dayEndMinutes, timeLabels } = useMemo(() => {
    let startMin = 7 * 60;
    let endMin = 21 * 60;

    for (const s of slots) {
      if (s.startTime) {
        const sm = timeToMinutes(s.startTime);
        if (sm < startMin) startMin = Math.floor(sm / 60) * 60;
      }
      if (s.endTime) {
        const em = timeToMinutes(s.endTime);
        if (em > endMin) endMin = Math.ceil(em / 60) * 60;
      }
    }

    const labels: { minutes: number; label: string }[] = [];
    for (let m = startMin; m <= endMin; m += 30) {
      labels.push({ minutes: m, label: formatTimeDisplay(minutesToTime(m)) });
    }

    return { dayStartMinutes: startMin, dayEndMinutes: endMin, timeLabels: labels };
  }, [slots]);

  // Compute active bands for compact mode
  const bands = useMemo((): TimeBand[] => {
    if (slots.length === 0) return [];

    // Build raw ranges from slot times with padding
    const ranges: { start: number; end: number }[] = [];
    for (const s of slots) {
      const sm = timeToMinutes(s.startTime || '');
      const em = timeToMinutes(s.endTime || '');
      if (sm === 0 && em === 0) continue;
      ranges.push({
        start: Math.max(0, sm - BAND_PADDING_MIN),
        end: em + BAND_PADDING_MIN,
      });
    }

    if (ranges.length === 0) return [];

    // Sort by start
    ranges.sort((a, b) => a.start - b.start);

    // Merge overlapping ranges
    const merged: { start: number; end: number }[] = [ranges[0]];
    for (let i = 1; i < ranges.length; i++) {
      const last = merged[merged.length - 1];
      if (ranges[i].start <= last.end) {
        last.end = Math.max(last.end, ranges[i].end);
      } else {
        merged.push({ ...ranges[i] });
      }
    }

    // Snap to 30-minute boundaries
    for (const r of merged) {
      r.start = Math.floor(r.start / 30) * 30;
      r.end = Math.ceil(r.end / 30) * 30;
    }

    // Build bands with yOffsets
    const result: TimeBand[] = [];
    let yOffset = 0;
    for (let i = 0; i < merged.length; i++) {
      if (i > 0) {
        yOffset += GAP_INDICATOR_HEIGHT;
      }
      const heightPx = ((merged[i].end - merged[i].start) / 30) * PIXELS_PER_30MIN;
      result.push({
        startMin: merged[i].start,
        endMin: merged[i].end,
        yOffset,
        heightPx,
      });
      yOffset += heightPx;
    }

    return result;
  }, [slots]);

  // Map a time (minutes) to a Y position in compact mode
  const compactY = useCallback((minutes: number): number => {
    for (const band of bands) {
      if (minutes >= band.startMin && minutes <= band.endMin) {
        return band.yOffset + ((minutes - band.startMin) / 30) * PIXELS_PER_30MIN;
      }
    }
    // If outside all bands (shouldn't happen for slots), clamp to nearest band
    if (bands.length > 0) {
      if (minutes < bands[0].startMin) return bands[0].yOffset;
      const last = bands[bands.length - 1];
      return last.yOffset + last.heightPx;
    }
    return 0;
  }, [bands]);

  // Total height depends on mode
  const totalHeight = useMemo(() => {
    if (!compactMode) {
      return ((dayEndMinutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN;
    }
    if (bands.length === 0) return 0;
    const last = bands[bands.length - 1];
    return last.yOffset + last.heightPx;
  }, [compactMode, dayStartMinutes, dayEndMinutes, bands]);

  // Time labels for compact mode
  const compactTimeLabels = useMemo(() => {
    if (!compactMode) return [];
    const labels: { y: number; label: string }[] = [];
    for (const band of bands) {
      for (let m = band.startMin; m <= band.endMin; m += 30) {
        labels.push({
          y: band.yOffset + ((m - band.startMin) / 30) * PIXELS_PER_30MIN,
          label: formatTimeDisplay(minutesToTime(m)),
        });
      }
    }
    return labels;
  }, [compactMode, bands]);

  // Grid lines for compact mode
  const compactGridLines = useMemo(() => {
    if (!compactMode) return [];
    const lines: { y: number; isHour: boolean }[] = [];
    for (const band of bands) {
      for (let m = band.startMin; m <= band.endMin; m += 30) {
        lines.push({
          y: band.yOffset + ((m - band.startMin) / 30) * PIXELS_PER_30MIN,
          isHour: m % 60 === 0,
        });
      }
    }
    return lines;
  }, [compactMode, bands]);

  // Gap indicators between bands
  const gapIndicators = useMemo(() => {
    if (!compactMode || bands.length <= 1) return [];
    const gaps: { y: number; durationMin: number }[] = [];
    for (let i = 1; i < bands.length; i++) {
      const gapStart = bands[i - 1].endMin;
      const gapEnd = bands[i].startMin;
      gaps.push({
        y: bands[i].yOffset - GAP_INDICATOR_HEIGHT,
        durationMin: gapEnd - gapStart,
      });
    }
    return gaps;
  }, [compactMode, bands]);

  // Position a slot
  const getSlotStyle = useCallback((slot: UnifiedSlot) => {
    const startMin = timeToMinutes(slot.startTime);
    const endMin = timeToMinutes(slot.endTime);

    if (compactMode) {
      const top = compactY(startMin);
      const bottom = compactY(endMin);
      const height = Math.max(bottom - top, 32);
      return { top, height };
    }

    const top = ((startMin - dayStartMinutes) / 30) * PIXELS_PER_30MIN;
    const height = Math.max(((endMin - startMin) / 30) * PIXELS_PER_30MIN, 32);
    return { top, height };
  }, [compactMode, compactY, dayStartMinutes]);

  // Overlap column assignment
  const slotColumns = useMemo(() => {
    const sorted = [...slots].sort((a, b) => timeToMinutes(a.startTime || '') - timeToMinutes(b.startTime || ''));
    const columns: { slot: UnifiedSlot; col: number; totalCols: number }[] = [];
    const active: { slot: UnifiedSlot; col: number; endMin: number }[] = [];

    for (const slot of sorted) {
      const startMin = timeToMinutes(slot.startTime || '');
      const endMin = timeToMinutes(slot.endTime || '');

      const stillActive = active.filter(a => a.endMin > startMin);
      const usedCols = new Set(stillActive.map(a => a.col));

      let col = 0;
      while (usedCols.has(col)) col++;

      stillActive.push({ slot, col, endMin });
      columns.push({ slot, col, totalCols: 0 });

      active.length = 0;
      active.push(...stillActive);
    }

    // Second pass: compute total columns
    for (const entry of columns) {
      const startMin = timeToMinutes(entry.slot.startTime || '');
      const endMin = timeToMinutes(entry.slot.endTime || '');
      let maxCol = entry.col;

      for (const other of columns) {
        const otherStart = timeToMinutes(other.slot.startTime || '');
        const otherEnd = timeToMinutes(other.slot.endTime || '');
        if (otherStart < endMin && otherEnd > startMin) {
          maxCol = Math.max(maxCol, other.col);
        }
      }
      entry.totalCols = maxCol + 1;
    }

    return columns;
  }, [slots]);

  // Auto-scroll to current time
  useEffect(() => {
    const todayKey = toDateKey(new Date());
    if (date === todayKey && gridRef.current) {
      const now = new Date();
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      if (nowMinutes >= dayStartMinutes && nowMinutes <= dayEndMinutes) {
        const scrollTop = compactMode
          ? compactY(nowMinutes) - 100
          : ((nowMinutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN - 100;
        gridRef.current.parentElement?.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
      }
    }
  }, [date, dayStartMinutes, dayEndMinutes, compactMode, compactY]);

  const dateObj = parseLocalDate(date);
  const dateLabel = dateObj.toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const todayKey = toDateKey(new Date());
  const isToday = date === todayKey;

  // Current time position
  const currentTimeY = useMemo(() => {
    if (!isToday) return null;
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    if (nowMinutes < dayStartMinutes || nowMinutes > dayEndMinutes) return null;
    if (compactMode) {
      // Check if current time falls within any band
      const inBand = bands.some(b => nowMinutes >= b.startMin && nowMinutes <= b.endMin);
      if (!inBand) return null;
      return compactY(nowMinutes);
    }
    return ((nowMinutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN;
  }, [isToday, compactMode, compactY, dayStartMinutes, dayEndMinutes, bands]);

  return (
    <div className="idash-day-view">
      {/* Date navigation */}
      <div className="idash-day-header">
        <button className="idash-nav-btn" onClick={() => onDateChange(addDays(date, -1))}>
          &lsaquo; Prev
        </button>
        <span className={`idash-day-title ${isToday ? 'is-today' : ''}`}>
          {dateLabel}
          {isToday && <span className="today-badge">TODAY</span>}
        </span>
        <button className="idash-nav-btn" onClick={() => onDateChange(addDays(date, 1))}>
          Next &rsaquo;
        </button>
      </div>

      <div className="idash-day-summary">
        <span>{slots.length} slot{slots.length !== 1 ? 's' : ''} on this day</span>
        {slots.length > 0 && (
          <button
            className={`idash-compact-toggle ${compactMode ? 'active' : ''}`}
            onClick={() => setCompactMode(!compactMode)}
            title={compactMode ? 'Show full timeline' : 'Collapse inactive hours'}
          >
            {compactMode ? 'Full View' : 'Compact'}
          </button>
        )}
      </div>

      {slots.length === 0 ? (
        <div className="idash-empty-day">
          No classes scheduled for this day.
        </div>
      ) : (
        <div className="idash-day-grid-wrapper">
          {/* Time labels */}
          <div className="idash-time-labels">
            {compactMode ? (
              <>
                {compactTimeLabels.map(({ y, label }, idx) => (
                  <div key={idx} className="idash-time-label" style={{ top: y }}>
                    {label}
                  </div>
                ))}
                {gapIndicators.map((gap, idx) => (
                  <div
                    key={`gap-label-${idx}`}
                    className="idash-gap-time-label"
                    style={{ top: gap.y, height: GAP_INDICATOR_HEIGHT }}
                  />
                ))}
              </>
            ) : (
              timeLabels.map(({ minutes, label }) => (
                <div
                  key={minutes}
                  className="idash-time-label"
                  style={{ top: ((minutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN }}
                >
                  {label}
                </div>
              ))
            )}
          </div>

          <div
            className="idash-time-grid"
            ref={gridRef}
            style={{ height: totalHeight }}
          >
            {/* Grid lines */}
            {compactMode ? (
              compactGridLines.map(({ y, isHour }, idx) => (
                <div
                  key={idx}
                  className={`idash-grid-line ${isHour ? 'hour-line' : 'half-hour-line'}`}
                  style={{ top: y }}
                />
              ))
            ) : (
              timeLabels.map(({ minutes }) => (
                <div
                  key={minutes}
                  className={`idash-grid-line ${minutes % 60 === 0 ? 'hour-line' : 'half-hour-line'}`}
                  style={{ top: ((minutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN }}
                />
              ))
            )}

            {/* Gap indicators (compact mode only) */}
            {compactMode && gapIndicators.map((gap, idx) => (
              <div
                key={`gap-${idx}`}
                className="idash-gap-indicator"
                style={{ top: gap.y, height: GAP_INDICATOR_HEIGHT }}
              >
                <span className="gap-line" />
                <span className="gap-text">{formatGapDuration(gap.durationMin)}</span>
                <span className="gap-line" />
              </div>
            ))}

            {/* Current time indicator */}
            {currentTimeY !== null && (
              <div className="idash-current-time" style={{ top: currentTimeY }} />
            )}

            {/* Slot cards */}
            {slotColumns.map(({ slot, col, totalCols }) => {
              const { top, height } = getSlotStyle(slot);
              const widthPct = 100 / totalCols;
              const leftPct = col * widthPct;
              const colors = STATUS_COLORS[slot.status] || STATUS_COLORS.planned;

              const lpPercent = slot.lessonPlanId && slot.lessonPlanStepCount
                ? Math.round(((slot.lessonPlanCompletedSteps || 0) / slot.lessonPlanStepCount) * 100)
                : 0;

              const hasActual = !!slot.actualSlotId;
              const actualBatchIds = slot.actualBatchIds || [];
              const actualBatchNames = slot.actualBatchNames || [];
              const timeDiffers = hasActual && (slot.actualStartTime !== slot.startTime || slot.actualEndTime !== slot.endTime);
              const topicDiffers = hasActual && slot.actualTopicId !== null && slot.actualTopicId !== slot.topicMasterId;
              const instructorDiffers = hasActual && slot.actualInstructorId !== null && slot.actualInstructorId !== slot.instructorId;
              const batchesDiffer = hasActual && actualBatchIds.length > 0
                && (actualBatchIds.length !== slot.batchIds.length || actualBatchIds.some(id => !slot.batchIds.includes(id)));

              return (
                <div
                  key={slot.templateSlotId}
                  className={`idash-slot-card status-${slot.status}`}
                  style={{
                    top,
                    height,
                    left: `${leftPct}%`,
                    width: `${widthPct - 1}%`,
                    backgroundColor: colors.bg,
                    borderLeftColor: colors.border,
                  }}
                >
                  <div className="slot-card-header">
                    <span className="slot-card-time">
                      {formatTimeDisplay(slot.startTime)} - {formatTimeDisplay(slot.endTime)}
                    </span>
                    <span className={`slot-status-badge ${slot.status}`}>{slot.status}</span>
                  </div>

                  {height > 40 && hasActual && (
                    <div className="slot-planned-vs-actual">
                      <div className="slot-column planned">
                        <div className="slot-column-header">PLANNED</div>
                        <div className="slot-column-row">
                          <span className="slot-column-label">Time:</span>
                          <span>{formatTimeDisplay(slot.startTime)} - {formatTimeDisplay(slot.endTime)}</span>
                        </div>
                        {slot.topicName && (
                          <div className="slot-column-row">
                            <span className="slot-column-label">Topic:</span>
                            <span>{slot.topicName}</span>
                          </div>
                        )}
                        {slot.instructorName && (
                          <div className="slot-column-row">
                            <span className="slot-column-label">Instructor:</span>
                            <span>{slot.instructorName}</span>
                          </div>
                        )}
                        {slot.batchNames.length > 0 && (
                          <div className="slot-column-row">
                            <span className="slot-column-label">Batches:</span>
                            <span>{slot.batchNames.join(', ')}</span>
                          </div>
                        )}
                      </div>
                      <div className="slot-column conducted">
                        <div className="slot-column-header">CONDUCTED</div>
                        <div className={`slot-column-row${timeDiffers ? ' slot-diff-highlight' : ''}`}>
                          <span className="slot-column-label">Time:</span>
                          <span>
                            {formatTimeDisplay(slot.actualStartTime || slot.startTime)} - {formatTimeDisplay(slot.actualEndTime || slot.endTime)}
                          </span>
                        </div>
                        <div className={`slot-column-row${topicDiffers ? ' slot-diff-highlight' : ''}`}>
                          <span className="slot-column-label">Topic:</span>
                          <span>{slot.actualTopicName || slot.topicName || '-'}</span>
                        </div>
                        <div className={`slot-column-row${instructorDiffers ? ' slot-diff-highlight' : ''}`}>
                          <span className="slot-column-label">Instr:</span>
                          <span>{slot.actualInstructorName || slot.instructorName || '-'}</span>
                        </div>
                        {(actualBatchNames.length > 0 || slot.batchNames.length > 0) && (
                          <div className={`slot-column-row${batchesDiffer ? ' slot-diff-highlight' : ''}`}>
                            <span className="slot-column-label">Batches:</span>
                            <span>{actualBatchNames.length > 0 ? actualBatchNames.join(', ') : slot.batchNames.join(', ')}</span>
                          </div>
                        )}
                        {slot.attendanceRate !== null && (
                          <div className="slot-column-row">
                            <span className="slot-column-label">Attendance:</span>
                            <span>{slot.presentCount}/{slot.studentCount} ({slot.attendanceRate}%)</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {height > 40 && !hasActual && (
                    <div className="slot-card-content">
                      <div className="slot-card-left">
                        {slot.topicName && <div className="slot-card-topic">{slot.topicName}</div>}
                        {slot.templateName && <div className="slot-card-template">{slot.templateName}</div>}
                        {slot.agenda && height > 80 && (
                          <div className="slot-card-agenda">{slot.agenda}</div>
                        )}
                      </div>
                      <div className="slot-card-right">
                        {slot.batchNames.length > 0 && (
                          <div className="slot-card-batches">{slot.batchNames.join(', ')}</div>
                        )}
                        {slot.lessonPlanId && (
                          <div className="slot-lp-progress">
                            <div className="slot-lp-bar">
                              <div className="slot-lp-fill" style={{ width: `${lpPercent}%` }} />
                            </div>
                            <span className="slot-lp-text">
                              LP {slot.lessonPlanCompletedSteps || 0}/{slot.lessonPlanStepCount || 0}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action bar - shown for all slots */}
                  {height > 40 && (
                    <div className="slot-card-actions">
                      <SlotActionBar
                        slot={slot}
                        compact={height < 80}
                        onAttendance={onAttendance}
                        onLessonPlan={onLessonPlan}
                        onViewTemplate={onViewTemplate}
                        onConfirm={onConfirm}
                        onUnconfirm={onUnconfirm}
                      />
                    </div>
                  )}

                  {!hasActual && slot.attendanceRate !== null && (
                    <div className="slot-attendance-badge">{slot.attendanceRate}%</div>
                  )}

                  {height <= 40 && (
                    <SlotActionBar
                      slot={slot}
                      compact
                      onAttendance={onAttendance}
                      onLessonPlan={onLessonPlan}
                      onViewTemplate={onViewTemplate}
                      onConfirm={onConfirm}
                      onUnconfirm={onUnconfirm}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardDayView;
